import React, { useRef } from 'react';

// Redux
import { shallowEqual } from 'react-redux';

// Classnames
import clsx from 'clsx';

// Lib
import { Compound } from 'lib/prices';
import { useWindowSize } from 'lib/hooks';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'lib/hooks/useDispatch';
import { convertDataPrices, convertedToArray, convertProjectsToOptions } from 'lib/helpers';

import {
	styleFieldSelectPrices, styleFieldSelectProject
} from 'pages/LibrariesCompounds/components/HeaderList';

// Components
import ListType from './ChangeView';
import SubHeaderList from './SubHeaderList';
import Button from 'components/UiKit/Button';
import FieldSelect from 'components/UiKit/FieldSelect';
import FieldCheckbox from 'components/UiKit/FieldCheckbox';

// Actions
import { openModal } from 'reducers/modal';
import { showMessage } from 'reducers/messages';
import * as actionsCreator from 'pages/Pfizer/reducer';
import * as actionsCreatorProject from 'pages/Projects/reducer';
import { handlerChangePriceApp, toggleModal } from 'reducers/settings';
import { clearAllCheckbox, changeCheckboxAll } from 'pages/Pfizer/reducer';

// Type
type TFuncArgs = {
	[index: string]: string | number;
}

interface FieldSelectRef {
	setValue: (value: any) => void;
}

const styleSelectProject = {
	...styleFieldSelectProject,
	container: (provided: any) => ({
		...provided,
		"&:hover, &:focus": {},
		height: "4rem",
		width: "10rem",
		color: "#060606",
		maxWidth: "100%",
		fontWeight: "500",
		fontSize: "1.4rem",
		fontStyle: "normal",
		lineHeight: "1.8rem",
		letterSpacing: "0.8px",
		fontFamily: "Poppins",

		"@media only screen and (max-width: 1024px)": {
			...provided["@media only screen and (max-width: 1024px)"],
			maxWidth: "100%",
			width: "100%"
		}
	}),
	control: (p: any) => ({
		...p,
		"&:hover, &:focus": {
			borderColor: "unset"
		},
		width: "27rem",
		height: "4rem",
		border: "unset",
		color: "#060606",
		boxShadow: "none",
		maxWidth: "100%",
		cursor: "pointer",
		fontWeight: "500",
		fontSize: "1.4rem",
		fontStyle: "normal",
		borderStyle: "unset",
		lineHeight: "1.8rem",
		borderRadius: "unset",
		letterSpacing: "0.8px",
		fontFamily: "Poppins",
		backgroundColor: "transparent",
		borderBottom: "1px solid rgba(6, 6, 6, 0.25)",

		"@media only screen and (max-width: 1024px)": {
			...p["@media only screen and (max-width: 1024px)"],
			maxWidth: "100%",
			width: "100%"
		}
	}),
};
const styleSelectPrepackage = {
	...styleFieldSelectPrices,
	container: (p: any) => ({
		...p,
		"&:hover, &:focus": {},
		height: "4rem",
		width: "27rem",
		color: "#060606",
		maxWidth: "100%",
		fontWeight: "500",
		fontSize: "1.4rem",
		fontStyle: "normal",
		lineHeight: "1.8rem",
		letterSpacing: "0.8px",
		fontFamily: "Poppins",

		"@media only screen and (max-width: 1024px)": {
			...p["@media only screen and (max-width: 1024px)"],
			maxWidth: "100%",
			width: "100%",
		}
	}),
	control: (p: any) => ({
		...p,
		"&:hover, &:focus": {
			borderColor: "unset"
		},
		width: "27rem",
		height: "4rem",
		border: "unset",
		color: "#060606",
		boxShadow: "none",
		maxWidth: "100%",
		cursor: "pointer",
		fontWeight: "500",
		fontSize: "1.4rem",
		fontStyle: "normal",
		borderStyle: "unset",
		lineHeight: "1.8rem",
		borderRadius: "unset",
		letterSpacing: "0.8px",
		fontFamily: "Poppins",
		backgroundColor: "transparent",
		borderBottom: "1px solid rgba(6, 6, 6, 0.25)",

		"@media only screen and (max-width: 1024px)": {
			...p["@media only screen and (max-width: 1024px)"],
			maxWidth: "100%",
			width: "100%",
		}
	})
};

export const HeaderList = () => {
	const ref = useRef<FieldSelectRef>(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const {
		user,
		currentPrice,
		currentProject,
		dataProjects,

		type,
		checkboxAll,
		categoriesById,
	} = useAppSelector(
		(store) => {
			return {
				user: store.settings.user,
				currentPrice: store.settings.currentPrice,
				currentProject: store.projects.currentProject,
				dataProjects: store.projects.dataProjects,

				type: store.pfizer.typeViewList,
				checkboxAll: store.pfizer.checkboxAll,
				categoriesById: store.pfizer.categoriesById
			};
		},
		shallowEqual
	);

	const handleChangePrices = (selected: TFuncArgs) => {
		dispatch(handlerChangePriceApp(selected));
		dispatch(actionsCreator.updatePricesAll(selected));
	};

	const handleChangeCheckboxAll = () => {
		dispatch(changeCheckboxAll());
	};

	const handleClearAllCheckboxSelected = () => {
		dispatch(clearAllCheckbox());
	};

	const handleAddToProject = () => {
		if (!user.authorized) {
			dispatch(toggleModal(true));
			return;
		}

		const uniqId = crypto.randomUUID();
		let result: any = [];

		for (const item of categoriesById || []) {
			const hasPrices = Object.values(item?.currentPrice)
				.filter(Boolean)
				.length;

			if (
				item.selected &&
				!hasPrices
			) {
				dispatch(showMessage({
					type: "info",
					uniqId: uniqId,
					text: "Choose price/mg"
				}));
				result = [];
				break;
			}

			if (
				item.selected &&
				hasPrices
			) result.push(item);
		}

		const newData = result.map((item: any) => {
			const supIds = item.supIds;
			const findElement = categoriesById?.find((elem: any) => elem?.id === item?.id);
			const prices = findElement.prices;
			const currentTypePrice = findElement.currentTypePrice;
			const findTypePrice = prices && prices.find(
				(x: any) => x.supplier === currentTypePrice
			) ? currentTypePrice : prices && prices[0]?.supplier;

			const findProductId = supIds?.find(
				(item: any) => item.supplier === currentTypePrice
			);
			return {
				...item,
				id: findProductId.supId,
				supplier: findTypePrice,
			}
		});

		if (currentProject && newData?.length) {
			dispatch(actionsCreatorProject.addItemToProject({
				data: newData,
				uniqId: uniqId,
				url: "/projects",
				id: currentProject,
				title: "Compound Added to project",
				text: "Review or continue browsing. Contact<br/> us for any questions. Thank you!"
			}));
			return;
		}

		const convertedProjects = convertedToArray(dataProjects);
		const itemsWithValidPrice = newData.filter(
			(item: any) => item.currentPrice && item.currentPrice.mg
		);

		if (!currentProject && itemsWithValidPrice.length) {
			const modalConfig = {
				data: newData,
				url: "/projects",
				navigate: navigate,
				isAddItem: "isAddItem",
				title: "Compound Added to project",
				type: convertedProjects.length ? "projectsList" : "projectAdd",
				text: "Review or continue browsing. Contact<br/> us for any questions. Thank you!"
			};

			dispatch(openModal(modalConfig));
		}
	};

	const handleChangeToggleFilter = () => {
		dispatch(actionsCreator.changeToggleFilter());
	};

	const handleConfirmChangePrice = (selected: TFuncArgs) => {
		dispatch(openModal(
			{
				data: selected,
				color: 'error',
				type: 'confirm',
				textClose: 'Cancel',
				textSubmit: 'Confirm',
				actionSubmit: handleChangePrices,
				innerClass: 'libraries-compounds__confirm',
				text: `The default prepackage size of all compounds will be set to ${selected.mg} mg`
			})
		);
	};

	const handleClearPriceAll = () => {
		if (ref.current) {
			ref.current.setValue([]);
		}
		dispatch(handlerChangePriceApp([]));

		const updatedPricesAll = categoriesById.map((item: any) => ({ ...item, currentPrice: {} }));
		dispatch(actionsCreator.updatePricesAll(updatedPricesAll));
	};

	const handleConfirmClearPrice = () => {
		dispatch(openModal(
			{
				color: "error",
				type: "confirm",
				textClose: "Cancel",
				textSubmit: "Confirm",
				actionSubmit: handleClearPriceAll,
				innerClass: "libraries-compounds__confirm",
				text: `The default prepackage size of all compounds will be set to manual selection.`
			})
		);
	};

	const handleChangeProject = (selected: TFuncArgs) => {
		dispatch(
			actionsCreatorProject.handlerChangeCurrentProject(selected.value)
		);
	};

	const { width } = useWindowSize();
	const isDesktop = +width < 1024;
	const isRow = type === 'row';
	const prices = categoriesById?.length ? categoriesById[0]?.prices[0].prices : [];
	const options = convertDataPrices(prices);
	const optionsProjects = convertProjectsToOptions(dataProjects);
	const project = currentProject
		? dataProjects?.find(item => item.id === +currentProject)
		: undefined;
	const valueProject = project
		? { value: project.id, label: project.name || '' }
		: undefined;

	const cnWrapper = clsx(
		'pfizer__header-list full-width',
		isRow ? 'pfizer__header-list--active pb10' : ''
	);
	const cnSelectCheckbox = clsx(
		!isDesktop ? 'pfizer__select-checkbox' : '',
	);
	const cnBlockSelect = clsx(
		'pfizer__block-select-number mr10',
		categoriesById?.length > 1000 ? 'pfizer__block-select-number--active' : '',
	);

	const selected = categoriesById.filter((item: Compound) => item.selected)?.length;
	const labelBtn = `Add ${selected} selected`;

	return (
		<div className={cnWrapper}>
			<div className="pfizer__header-list-top flex flex-ai-end flex-jc-between full-width mb20 flex-direction-column-desktop">
				<div className="pfizer__header-select flex flex-ai-end">
					{!isRow && <FieldCheckbox
						readOnly
						label="All"
						value={checkboxAll}
						name="checkboxItemAll"
						className={cnSelectCheckbox}
						onChange={handleChangeCheckboxAll}
					/>}

					<div className="flex flex-ai-start">
						<div className="pfizer__block-select relative flex flex-direction-column flex-jc-between mr30">
							<p className="pfizer__block-select-title mb10">Selected</p>
							<div className="flex flex-ai-center">
								<p className={cnBlockSelect}>
									{selected} of {categoriesById?.length}
								</p>

								<Button
									isSvg
									color="error"
									variable="icon"
									icon="deleteSvg"
									title="Clear checkbox all"
									className="pfizer__select-clear"
									onPress={handleClearAllCheckboxSelected}
								/>
							</div>
						</div>

						<Button
							isSvg
							title="Filter"
							variable="icon"
							icon="filterSvg"
							onPress={handleChangeToggleFilter}
							className="pfizer__header-select-icon display-block-mobile"
						/>
					</div>

					<ListType />
					<div
						className="flex flex-ai-end"
						id="categorized-products-package"
					>
						<FieldSelect
							name="projectName"
							value={valueProject}
							placeholder="Project"
							options={optionsProjects}
							styles={styleSelectProject}
							disabled={!user.authorized}
							onChange={handleChangeProject}
							className="align-self-end ml5 pfizer__select-project"
						/>
						<FieldSelect
							required
							customRef={ref}
							options={options}
							label="Prepackage"
							name="pricesListAll"
							value={currentPrice}
							placeholder="Prepack"
							styles={styleFieldSelectPrices}
							title="Prepackage for seleted compounds"
							classNameLabel="pfizer__prices-list-label"
							onChange={(value: TFuncArgs, opt: TFuncArgs) => {
								if (opt.action === 'select-option') {
									handleConfirmChangePrice(value);
								}
							}}
							className="pfizer__prices-list pfizer__select-prepackage ml10"
						/>
						<Button
							isSvg
							variable="icon"
							color="error"
							icon="deleteSvg"
							title="Clear prepackage all"
							onPress={handleConfirmClearPrice}
							disabled={!(!!Object.values(currentPrice)?.length)}
							className="ml10 align-self-end pfizer__select-prepackage"
						/>
					</div>
				</div>

				<div className="flex display-block-mobile full-width mb20">
					<FieldSelect
						name="projectName"
						value={valueProject}
						placeholder="Project"
						className='full-width mr5'
						options={optionsProjects}
						styles={styleSelectProject}
						disabled={!user.authorized}
						onChange={handleChangeProject}
					/>
					<FieldSelect
						required
						customRef={ref}
						options={options}
						name="pricesListAll"
						value={currentPrice}
						placeholder="Prepack"
						styles={styleSelectPrepackage}
						className="pfizer__prices-list full-width"
						title="Prepackage for seleted compounds"
						classNameLabel="pfizer__prices-list-label"
						onChange={(value: TFuncArgs, opt: TFuncArgs) => {
							if (opt.action === 'select-option') {
								handleConfirmChangePrice(value);
							}
						}}
					/>
				</div>

				<Button
					variable="info"
					label={labelBtn}
					disabled={!selected}
					onPress={handleAddToProject}
					id="categorized-products-package-button"
					className="align-self-end pfizer__btn-add-project"
				/>
			</div>

			{isRow && <SubHeaderList
				checkboxAll={checkboxAll}
				handleChangeCheckboxAll={handleChangeCheckboxAll}
			/>}
		</div>
	);
};

export default HeaderList;
